import { Link, useLocation } from 'react-router-dom';
import {
  Map as MapIcon,
  Bell,
  Calendar,
  MessageCircle,
  LayoutList,
  Target,
  Users,
  Navigation2,
} from 'lucide-react';
import { useLiveRsvpEvent } from '../hooks/queries/useLiveRsvpEvent';
import { usePromptAuth } from './auth/AuthPrompt';
import { Capacitor } from '@capacitor/core';
import { useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { motionFlags } from '../lib/motionFlags';
import { useQueryClient } from '@tanstack/react-query';
import { requestDeviceOrientationPermission } from '../lib/proximity/runtime';
import { useCurrentUserProfile } from '../contexts/CurrentUserProfileContext';
import { supabase } from '../supabaseClient';
import {
  removeTrackedChannel,
  subscribeWithHealth,
  freshTopic,
  getChannelStatus,
} from '../lib/realtime/subscriptionHealth';
import { registerRealtimeResumeHandler } from '../lib/realtime/resumeRegistry';
import { registerRealtimeTeardownHandler } from '../lib/realtime/teardownRegistry';
import {
  emitNotificationRow,
  FIND_PEER_NOTIFICATION_TYPES,
  MEMBERSHIP_NOTIFICATION_TYPES,
} from '../lib/realtime/notificationEvents';
import { requestFindPeersRefresh } from '../lib/proximity/data/findPeersRefresh';
import Avatar from './Avatar';
import Button from './ui/Button';
import { useProfileBundle } from '../hooks/queries/useProfileQueries';
import { useUnreadNotificationCount } from '../hooks/queries/useNotificationsQueries';
import { queryKeys } from '../lib/query/queryKeys';
import PriiLogo, { PriiWordmark } from './PriiLogo';
import { GLASS_SURFACES, HEADER_SOLID_SURFACE_CLASS } from '../config/glassSurfaces';
import { SURFACES, GROUPS_INDEX_PATH } from '../config/productSurfaces';
import { isWelcomeDemo } from '../lib/welcomeDemo';
import { ringColor, useHues } from '../lib/map/faceStyle';

/** How often the header re-reads notifications and your groups while its realtime channel is not live. */
const NOTIFICATIONS_POLL_MS = 30_000;

const IS_NATIVE = Capacitor.isNativePlatform();

/**
 * Each tab reopens where you left it (operator, 2026-09-23: Maps -> Find -> Maps should still be on Portola, in the
 * same view). The last path per tab, for this app session; tapping the tab you are already on goes to its list.
 */
const TAB_ROOTS = { sets: '/sets', map: '/map', groups: '/groups' };
const lastTabPath = {};
function tabTarget(key, active) {
  const root = TAB_ROOTS[key];
  if (!root) return null;
  return active ? root : (lastTabPath[key] ?? root);
}

/** Dev only: the dock's ?showfind / ?findlit switches, kept for the tab session so they survive a tab tap. */
function devDockParams() {
  const q = new URLSearchParams(window.location.search);
  const keys = ['showfind', 'findlit'];
  try {
    for (const k of keys) if (q.has(k)) window.sessionStorage.setItem(`prii.dev.${k}`, q.get(k));
    for (const k of keys) {
      const v = window.sessionStorage.getItem(`prii.dev.${k}`);
      if (v != null && !q.has(k)) q.set(k, v);
    }
  } catch {
    /* private mode: the URL alone */
  }
  return q;
}

export default function Header() {
  useHues(); // your ring colour (Edit Profile) redraws the corner avatar
  const location = useLocation();
  const queryClient = useQueryClient();
  const { profile, session, authReady } = useCurrentUserProfile();
  const promptAuth = usePromptAuth();
  const { data: ownProfileBundle } = useProfileBundle(session?.user?.id, session?.user?.id);
  const { data: unreadCount = 0 } = useUnreadNotificationCount(session?.user?.id);
  const notificationsChannelRef = useRef(null);

  const navProfilePhotoUrl =
    ownProfileBundle?.profile?.profile_photo_url ?? profile?.profile_photo_url ?? null;
  const displayName =
    ownProfileBundle?.profile?.display_name?.trim() ||
    profile?.display_name?.trim() ||
    session?.user?.email?.split('@')[0] ||
    '?';

  const isEvents = location.pathname === '/' || location.pathname.startsWith('/events/'); // an event's own page too, the way a festival's sets keep Sets lit (Sam, 2026-10-07)
  // With chat off, /groups is its own tab; with chat on, groups live inside Chats.
  const isChats =
    location.pathname === '/chats' ||
    location.pathname.startsWith('/chats/dm') ||
    location.pathname.startsWith('/groups');
  const isGroups = location.pathname === '/groups' || location.pathname.startsWith('/groups/');
  const isFind = location.pathname === '/find' || location.pathname.startsWith('/find/');
  const isSets = location.pathname === '/sets' || location.pathname.startsWith('/sets/');
  const isMap = location.pathname === '/map' || location.pathname.startsWith('/map/');
  const isNotifications = location.pathname.startsWith('/notifications');
  const isProfile = location.pathname.startsWith('/profile');

  useEffect(() => {
    // The welcome flow's pages are a show: a tab never comes back to them (Sam, 2026-10-26: after the tour, Maps and
    // Sets reopened Portola's tour pages).
    if (isWelcomeDemo(location.search)) return;
    const path = `${location.pathname}${location.search}`;
    for (const [key, root] of Object.entries(TAB_ROOTS)) {
      if (location.pathname === root || location.pathname.startsWith(`${root}/`))
        lastTabPath[key] = path;
    }
  }, [location.pathname, location.search]);

  const subscribeHeaderNotifications = useCallback(() => {
    if (!session?.user?.id) return undefined;

    const userId = session.user.id;

    const channelName = `header-notifications:${userId}`;
    if (notificationsChannelRef.current) {
      removeTrackedChannel(supabase, notificationsChannelRef.current, channelName);
      notificationsChannelRef.current = null;
    }

    const channel = supabase.channel(freshTopic(channelName)).on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${userId}`,
      },
      (payload) => {
        queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unreadCount(userId) });
        queryClient.invalidateQueries({ queryKey: ['notifications', 'list', userId] });
        const row = payload?.eventType === 'INSERT' ? payload.new : null;
        if (!row) return;
        if (MEMBERSHIP_NOTIFICATION_TYPES.has(row.type)) {
          // Approved into a group on someone else's phone: this device's "my groups" list is the only copy that
          // can go stale, and the Groups tab, Find and Maps all read it.
          queryClient.invalidateQueries({ queryKey: ['find'] });
          queryClient.invalidateQueries({ queryKey: ['groups'] });
          // An approval also writes your going RSVP on the server (approve_join_request): your RSVP lists, profile
          // events and Maps' list would otherwise wait out their 10-20 min stale times (2026-10-27 latency audit).
          queryClient.invalidateQueries({ queryKey: queryKeys.rsvps.mine(userId) });
          queryClient.invalidateQueries({ queryKey: queryKeys.profiles.events(userId) });
          queryClient.invalidateQueries({ queryKey: queryKeys.festivalMap.mine(userId) });
          // Someone joined a group you share for the event: watch them now, not on the 45 s timer (2026-10-14).
          requestFindPeersRefresh(`notification:${row.type}`);
        } else if (FIND_PEER_NOTIFICATION_TYPES.has(row.type)) {
          // Someone followed you (now mutual: watch them) or unfollowed you (no longer mutual: stop) - now, not in 45 s.
          requestFindPeersRefresh(`notification:${row.type}`);
        }
        emitNotificationRow(row);
      },
    );
    subscribeWithHealth(channel, channelName);
    notificationsChannelRef.current = channel;

    return channelName;
  }, [queryClient, session?.user?.id]);

  useEffect(() => {
    if (!session?.user?.id) return undefined;

    const channelName = subscribeHeaderNotifications();
    const unregisterTeardown = registerRealtimeTeardownHandler(() => {
      if (notificationsChannelRef.current && channelName) {
        removeTrackedChannel(supabase, notificationsChannelRef.current, channelName);
        notificationsChannelRef.current = null;
      }
    });
    const unregisterResume = registerRealtimeResumeHandler(() => {
      subscribeHeaderNotifications();
    });

    return () => {
      unregisterTeardown();
      unregisterResume();
      if (notificationsChannelRef.current && channelName) {
        removeTrackedChannel(supabase, notificationsChannelRef.current, channelName);
        notificationsChannelRef.current = null;
      }
    };
  }, [session?.user?.id, subscribeHeaderNotifications]);

  // While the notifications channel is not confirmed live, ask every 30 s instead (2026-10-27 latency audit): nothing
  // else re-reads notifications or your groups while the app stays open, and Find never remounts, so one missed event
  // left you "not in a group" for 10+ minutes. A new unread count also brings its toast (NotificationToast keys on it).
  useEffect(() => {
    const userId = session?.user?.id;
    if (!userId) return undefined;
    const channelName = `header-notifications:${userId}`;
    const t = window.setInterval(() => {
      if (document.visibilityState !== 'visible' || getChannelStatus(channelName) === 'healthy')
        return;
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unreadCount(userId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.find.groups(userId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.rsvps.mine(userId) });
    }, NOTIFICATIONS_POLL_MS);
    return () => window.clearInterval(t);
  }, [queryClient, session?.user?.id]);

  const handleFindNavClick = useCallback(() => {
    if (IS_NATIVE) {
      void requestDeviceOrientationPermission();
    }
  }, []);

  // The dock's tabs in order; --dock-active drives the sliding pill. Profile is the avatar in the top bar, not a tab
  // (operator, 2026-09-23): five tabs is the most a phone dock carries, and the product ones win the slots.
  const dockTabs = [
    { key: 'events', to: '/', label: 'Events', Icon: Calendar, active: isEvents },
    ...(SURFACES.chats
      ? [{ key: 'chats', to: '/chats', label: 'Chats', Icon: MessageCircle, active: isChats }]
      : []),
    ...(SURFACES.sets
      ? [{ key: 'sets', to: '/sets', label: 'Sets', Icon: LayoutList, active: isSets }]
      : []),
    { key: 'map', to: '/map', label: 'Maps', Icon: MapIcon, active: isMap },
    ...(IS_NATIVE
      ? [
          {
            key: 'find',
            to: '/find',
            label: 'Find',
            Icon: Target,
            active: isFind,
            onClick: handleFindNavClick,
          },
        ]
      : []),
    ...(!SURFACES.chats
      ? [{ key: 'groups', to: GROUPS_INDEX_PATH, label: 'Groups', Icon: Users, active: isGroups }]
      : []),
  ];
  // Find in the middle of the dock (Stage 7, 2026-10-23; set into the dock, Stage 5 2026-10-26): a round glass button, quiet
  // when nothing is on, lit - the primary button's glass, lighter - while an event you are going to is on
  // (useLiveRsvpEvent: the same window the solo Find session runs in), always there to tap. Styles in index.css
  // (.app-tab-dock__findbtn). A dev build: ?findlit=1 / 0 forces lit, ?showfind=1 shows Find in a browser.
  const devQ = import.meta.env.DEV && typeof window !== 'undefined' ? devDockParams() : null;
  const { event: liveEvent } = useLiveRsvpEvent(session?.user?.id, { authReady });
  const findLit =
    devQ?.get('findlit') === '1' ? true : devQ?.get('findlit') === '0' ? false : Boolean(liveEvent);
  {
    let i = dockTabs.findIndex((t) => t.key === 'find');
    if (i < 0 && devQ?.get('showfind') === '1') {
      dockTabs.push({
        key: 'find',
        to: '/find',
        label: 'Find',
        Icon: Target,
        active: isFind,
        onClick: handleFindNavClick,
      });
      i = dockTabs.length - 1;
    }
    if (i >= 0) {
      const find = dockTabs.splice(i, 1)[0];
      dockTabs.splice(Math.floor(dockTabs.length / 2), 0, find);
    }
  }
  const dockActive = dockTabs.findIndex((tab) => tab.active);
  // Double-tapping a tab takes its page back to the top (Sam, 2026-10-23). The first tap may also be the one that
  // switched to the tab; the second finds it active and scrolls the page (<main> is the app's scroller).
  // Tapping the tab you are already on takes its page back to the top - one tap, like every iPhone app (Sam, 2026-10-27:
  // the double tap of 10-23 was a misread). Only on the tab's own page: inside an event, Events goes back to the list.
  // The logo and the Events link do the same on Events.
  const scrollPageTop = () =>
    document.querySelector('main')?.scrollTo({ top: 0, behavior: 'smooth' });
  const onDockTap = (tab) => {
    tab.onClick?.();
    const root = tab.key === 'events' ? '/' : (TAB_ROOTS[tab.key] ?? tab.to);
    if (tab.active && location.pathname === root) scrollPageTop();
  };
  const onHomeTap = () => {
    if (location.pathname === '/') scrollPageTop();
  };
  // How the dock moves to a new tab (Stage 3, Sam 2026-10-21): slide (the pill glides - it only could once the app
  // stopped remounting on every page), spring (overshoots and settles, the icon pops), stretch (the pill reaches for the
  // new tab and its back edge catches up), fade (no travel: out here, in there, the icon pops). ?dock= in a dev build.
  const dockStyle = motionFlags().dock;
  const pillRef = useRef(null);
  const prevDockRef = useRef(dockActive);
  useLayoutEffect(() => {
    const from = prevDockRef.current;
    prevDockRef.current = dockActive;
    const pill = pillRef.current;
    if (!pill?.animate || from < 0 || dockActive < 0 || from === dockActive) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const w = pill.offsetWidth;
    if (dockStyle === 'stretch') {
      // In px on left/width (no scale: a scaled pill thickens its ring). The lead edge goes first, the back edge follows.
      const a = Math.min(from, dockActive) * w,
        z = (Math.max(from, dockActive) + 1) * w;
      pill.animate(
        [
          { transform: 'none', left: `${from * w}px`, width: `${w}px` },
          { transform: 'none', left: `${a}px`, width: `${z - a}px`, offset: 0.45 },
          { transform: 'none', left: `${dockActive * w}px`, width: `${w}px` },
        ],
        { duration: 420, easing: 'cubic-bezier(0.3, 0.7, 0.2, 1)' },
      );
    } else if (dockStyle === 'fade') {
      pill.animate(
        [
          { opacity: 0, transform: `translateX(${dockActive * 100}%) scale(0.9)` },
          { opacity: 1, transform: `translateX(${dockActive * 100}%) scale(1)` },
        ],
        { duration: 220, easing: 'ease-out' },
      );
    }
  }, [dockActive, dockStyle]);

  return (
    <>
      <header
        className={`app-header fixed inset-x-0 top-0 z-50 transition-colors inset-app-header ${GLASS_SURFACES.header ? '' : HEADER_SOLID_SURFACE_CLASS}`}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          {/* Mobile top bar: mark left, the name centred, your avatar right */}
          <div className="relative flex md:hidden items-center justify-between h-14">
            <Link
              to="/"
              onClick={onHomeTap}
              className="flex items-center gap-1.5 text-gray-900 dark:text-dark-text hover:opacity-90 transition-opacity"
              aria-label="Go to Events"
            >
              <PriiLogo size="sm" bare />
            </Link>

            <Link
              to="/"
              onClick={onHomeTap}
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
              aria-hidden="true"
              tabIndex={-1}
            >
              <PriiWordmark size="sm" />
            </Link>

            {/* Bell left of the avatar (operator, 2026-10-04 - reversing 2026-09-23's one-control corner): the
                unread count lives on the bell, not the avatar, and Profile no longer has a bell of its own. Purple, not grey:
                it sits back into the header (operator pick). */}
            {session || (import.meta.env.DEV && /[?&]sample=/.test(window.location.search)) ? (
              <div className="flex items-center gap-1">
                <Link
                  to="/notifications"
                  className={`header-bell relative flex items-center justify-center min-h-[44px] min-w-[44px] shrink-0${
                    isNotifications ? ' header-bell--on' : ''
                  }`}
                  aria-label={
                    unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'
                  }
                  aria-current={isNotifications ? 'page' : undefined}
                >
                  <span className="header-bell__icon">
                    <Bell className="h-[22px] w-[22px]" aria-hidden />
                  </span>
                  {unreadCount > 0 && (
                    <span className="status-badge absolute top-1 right-0.5">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </Link>
                <Link
                  to="/profile"
                  className="relative flex items-center justify-center min-h-[44px] min-w-[44px] shrink-0"
                  aria-label="Profile"
                  aria-current={isProfile ? 'page' : undefined}
                >
                  <span
                    className={`header-profile relative rounded-full ${isProfile ? 'header-profile--on' : ''}`}
                    style={{ '--pp-ring': ringColor(session ? displayName : '?') }}
                  >
                    <Avatar
                      displayName={session ? displayName : '?'}
                      profilePhotoUrl={session ? navProfilePhotoUrl : null}
                      size="xs"
                      loading="eager"
                    />
                  </span>
                </Link>
              </div>
            ) : (
              // Signed out: say what the corner does (operator, 2026-09-23: it was a "?" avatar),
              // and say it in the primary colour - signing in is the one thing we want from
              // someone who has not (operator, 2026-09-24).
              <Button
                size="sm"
                className="shrink-0 !rounded-full !px-4"
                onClick={() => promptAuth({ mode: 'login' })}
              >
                {' '}
                {/* the sign-in pop-up over the page (Stage 8), on Log in (Sam, 2026-10-23: "Sign in" read as either) */}
                Log in
              </Button>
            )}
          </div>

          {/* Desktop top bar (unchanged) */}
          <div className="hidden md:flex items-center justify-between h-14 sm:h-16">
            <div className="flex items-center gap-2 shrink-0">
              <Link
                to="/"
                onClick={onHomeTap}
                className="flex items-center gap-2 text-gray-900 dark:text-dark-text hover:opacity-90 transition-opacity"
                aria-label="Go to Events"
              >
                <PriiLogo size="md" wordmark bare />
              </Link>
            </div>

            <nav className="flex items-center gap-0.5 sm:gap-1">
              <Link
                to="/"
                onClick={onHomeTap}
                className={`px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                  isEvents
                    ? 'bg-primary/15 text-primary'
                    : 'text-gray-600 dark:text-dark-muted hover:bg-gray-100 dark:hover:bg-dark-border hover:text-gray-900 dark:hover:text-dark-text'
                }`}
              >
                <span className="inline-flex items-center gap-1.5">
                  <Calendar className="w-4 h-4" />
                  Events
                </span>
              </Link>
              {SURFACES.chats && (
                <Link
                  to="/chats"
                  className={`px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                    isChats
                      ? 'bg-primary/15 text-primary'
                      : 'text-gray-600 dark:text-dark-muted hover:bg-gray-100 dark:hover:bg-dark-border hover:text-gray-900 dark:hover:text-dark-text'
                  }`}
                >
                  <span className="inline-flex items-center gap-1.5">
                    <MessageCircle className="w-4 h-4" />
                    Chats
                  </span>
                </Link>
              )}
              {SURFACES.sets && (
                <Link
                  to="/sets"
                  className={`px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                    isSets
                      ? 'bg-primary/15 text-primary'
                      : 'text-gray-600 dark:text-dark-muted hover:bg-gray-100 dark:hover:bg-dark-border hover:text-gray-900 dark:hover:text-dark-text'
                  }`}
                >
                  <span className="inline-flex items-center gap-1.5">
                    <LayoutList className="w-4 h-4" />
                    Sets
                  </span>
                </Link>
              )}
              {!SURFACES.chats && (
                <Link
                  to={GROUPS_INDEX_PATH}
                  className={`px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                    isGroups
                      ? 'bg-primary/15 text-primary'
                      : 'text-gray-600 dark:text-dark-muted hover:bg-gray-100 dark:hover:bg-dark-border hover:text-gray-900 dark:hover:text-dark-text'
                  }`}
                >
                  <span className="inline-flex items-center gap-1.5">
                    <Users className="w-4 h-4" />
                    Groups
                  </span>
                </Link>
              )}
              <Link
                to="/map"
                className={`px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                  isMap
                    ? 'bg-primary/15 text-primary'
                    : 'text-gray-600 dark:text-dark-muted hover:bg-gray-100 dark:hover:bg-dark-border hover:text-gray-900 dark:hover:text-dark-text'
                }`}
              >
                <span className="inline-flex items-center gap-1.5">
                  <MapIcon className="w-4 h-4" />
                  Maps
                </span>
              </Link>
              {IS_NATIVE && (
                <Link
                  to="/find"
                  onClick={handleFindNavClick}
                  className={`px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                    isFind
                      ? 'bg-primary/15 text-primary'
                      : 'text-gray-600 dark:text-dark-muted hover:bg-gray-100 dark:hover:bg-dark-border hover:text-gray-900 dark:hover:text-dark-text'
                  }`}
                >
                  <span className="inline-flex items-center gap-1.5">
                    <Target className="w-4 h-4" />
                    Find
                  </span>
                </Link>
              )}
              <Link
                to="/notifications"
                className={`relative px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                  isNotifications
                    ? 'bg-primary/15 text-primary'
                    : 'text-gray-600 dark:text-dark-muted hover:bg-gray-100 dark:hover:bg-dark-border hover:text-gray-900 dark:hover:text-dark-text'
                }`}
              >
                <span className="inline-flex items-center gap-1.5">
                  <Bell className="w-4 h-4" />
                  Notifications
                </span>
                {unreadCount > 0 && (
                  <span className="status-badge absolute -top-1 -right-1">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </Link>
            </nav>

            <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
              {session ? (
                <Link
                  to="/profile"
                  className="flex items-center justify-center hover:opacity-90 transition-opacity rounded-full ring-2 ring-gray-200/80 dark:ring-dark-border shadow-sm focus:outline-none focus:ring-2 focus:ring-primary min-h-[44px] min-w-[44px]"
                  title={session.user?.email}
                >
                  <Avatar
                    displayName={displayName}
                    profilePhotoUrl={navProfilePhotoUrl}
                    size="xs"
                    loading="eager"
                  />
                </Link>
              ) : (
                // Says what it does (Sam, 2026-10-23: the "?" read as either sign up or log in). The same pop-up as Join, on
                // Log in - its switch is one tap from Sign up.
                <Button
                  size="sm"
                  className="shrink-0 !rounded-full !px-4"
                  onClick={() => promptAuth({ mode: 'login' })}
                >
                  Log in
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile tab dock — floats clear of the edges; one accent pill slides to the active tab (see .app-tab-dock in index.css) */}
      <nav
        className={`app-tab-dock app-tab-dock--${dockStyle} md:hidden ${dockActive < 0 ? 'app-tab-dock--idle' : ''}${dockTabs[dockActive]?.key === 'find' ? ' app-tab-dock--onfind' : ''}`}
        style={{ '--dock-tabs': dockTabs.length, '--dock-active': Math.max(dockActive, 0) }}
        aria-label="Primary"
      >
        <div className="app-tab-dock__tabs">
          <span ref={pillRef} className="app-tab-dock__pill" aria-hidden="true" />
          {dockTabs.map((tab) => (
            <Link
              key={tab.key}
              to={tabTarget(tab.key, tab.active) ?? tab.to}
              // Re-tapping Sets or Maps goes to its list and stays there. Without `stay`, on a festival day the list
              // redirected straight back to the festival, so the tab looked dead (2026-09-28).
              state={tab.active && TAB_ROOTS[tab.key] ? { stay: true } : undefined}
              onClick={() => onDockTap(tab)}
              className={`app-tab-dock__tab ${tab.active ? 'app-tab-dock__tab--on' : ''}${tab.key === 'find' ? ` app-tab-dock__find${findLit ? ' app-tab-dock__find--lit' : ''}` : ''}`}
              aria-current={tab.active ? 'page' : undefined}
            >
              {tab.key === 'find' ? (
                <span className="app-tab-dock__findbtn">
                  <Navigation2 className="w-[26px] h-[26px]" />
                </span>
              ) : (
                <tab.Icon className="w-[22px] h-[22px]" />
              )}
              <span>{tab.label}</span>
            </Link>
          ))}
        </div>
      </nav>
    </>
  );
}
